import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
  Alert, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControlLabel, IconButton, InputAdornment,
  MenuItem, Select, TextField, Tooltip, Typography
} from '@mui/material';
import {Add, Edit, Refresh, Search} from '@mui/icons-material';

import {
  listTenantRoutes, setOperatorRoute, removeOperatorRoute,
  listOperators, bulkSetOperatorRoute
} from '../../actions/manageOperators';
import Table from '../../components/Table';

class TenantRouting extends Component {
  constructor() {
    super();
    this.state = {
      tenants: [],
      total: 0,
      operators: [],
      loading: true,
      page: 1,
      pageSize: 50,
      searchText: '',
      showAssignDialog: false,
      assignTenantId: '',
      assignTenantName: '',
      assignOperatorId: '',
      assignPriority: 1,        // primary by default; openAssignDialog derives the smart default
      assignExistingRoutes: [], // populated when opening the dialog for a tenant that already has route(s)
      assignLockMessageType: false, // true when adding a failover for a specific type (per-line +) or reassigning
      // Reassign ("edit") state — operator is part of the unique key, so a
      // reassign is "set new operator at the same type+priority, then delete
      // the old route". editRouteId/editRouteSmscId identify the route being
      // replaced so we can chain the delete and exclude it from the picker.
      editMode: false,
      editRouteId: null,
      editRouteSmscId: '',
      showWait: false,
      // Bulk assign state
      showBulkDialog: false,
      bulkOperatorId: '',
      bulkExcludeRouted: true
    };
    this.tenantsReq = true;
    this.operatorsReq = true;
    this.setRouteReq = false;
    this.removeRouteReq = false;
    this.bulkRouteReq = false;
    this.searchTimer = null;
    // When a reassign's "set new operator" succeeds, this holds the old
    // route id to delete next (the second leg of the reassign chain).
    this.pendingRemoveRouteId = null;
  }

  componentDidMount() {
    this.props.listOperators();
    this.fetchTenants();
  }

  fetchTenants = () => {
    this.tenantsReq = true;
    this.props.listTenantRoutes(this.state.searchText, this.state.page, this.state.pageSize);
  }

  componentWillReceiveProps(nextProps) {
    if (this.tenantsReq && nextProps.tenantRoutesRes) {
      this.tenantsReq = false;
      if (nextProps.tenantRoutesRes.success) {
        const res = nextProps.tenantRoutesRes;
        const d = res.data || res;
        this.setState({
          tenants: d.tenants || [],
          total: d.total || 0,
          loading: false
        });
      } else {
        this.setState({loading: false});
      }
    }
    if (this.operatorsReq && nextProps.operatorsList?.success) {
      this.operatorsReq = false;
      this.setState({operators: nextProps.operatorsList.data?.operators || []});
    }
    if (this.setRouteReq && nextProps.setRouteRes) {
      this.setRouteReq = false;
      if (nextProps.setRouteRes.success) {
        if (this.pendingRemoveRouteId) {
          // Reassign, leg 2: the new operator is in place at the same
          // type+priority — now delete the route the old operator held.
          // Keep the wait spinner up; removeRouteRes closes the dialog.
          const rid = this.pendingRemoveRouteId;
          this.pendingRemoveRouteId = null;
          this.removeRouteReq = true;
          this.props.removeOperatorRoute(rid);
        } else {
          this.fetchTenants();
          this.setState({showAssignDialog: false, showWait: false});
        }
      } else {
        this.pendingRemoveRouteId = null;
        alert(nextProps.setRouteRes.message || nextProps.setRouteRes.error || 'Failed to set route');
        this.setState({showWait: false});
      }
    }
    if (this.removeRouteReq && nextProps.removeRouteRes) {
      this.removeRouteReq = false;
      if (nextProps.removeRouteRes.success) {
        this.fetchTenants();
      } else {
        alert(nextProps.removeRouteRes.message || nextProps.removeRouteRes.error || 'Failed to remove route');
      }
      // Close the assign dialog too — a reassign chain ends here.
      this.setState({showWait: false, showAssignDialog: false});
    }
    if (this.bulkRouteReq && nextProps.bulkSetRouteRes) {
      this.bulkRouteReq = false;
      if (nextProps.bulkSetRouteRes.success) {
        const d = nextProps.bulkSetRouteRes.data || {};
        alert(`Bulk assignment complete: ${d.assigned_count || d.assignedCount || 0} tenants assigned, ${d.skipped_count || d.skippedCount || 0} skipped`);
        this.fetchTenants();
        this.setState({showBulkDialog: false, showWait: false});
      } else {
        alert(nextProps.bulkSetRouteRes.message || nextProps.bulkSetRouteRes.error || 'Bulk assignment failed');
        this.setState({showWait: false});
      }
    }
  }

  handleSearch = (e) => {
    const searchText = e.target.value;
    this.setState({searchText, page: 1});
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.tenantsReq = true;
      this.setState({loading: true});
      this.props.listTenantRoutes(searchText, 1, this.state.pageSize);
    }, 400);
  }

  handleRefresh = () => {
    this.setState({loading: true});
    this.fetchTenants();
  }

  openAssignDialog = (row, lockedType = null) => {
    // Derive a smart default priority from the tenant's existing routes.
    // When lockedType is set (per-line "+ add failover"), scope the default to
    // that message type so the new operator becomes the next standby for it:
    //   - no routes (for the type) yet → priority = 1 (becomes the primary)
    //   - already has routes          → priority = max(existing) + 1 (failover/standby)
    // Admin can still override in the dialog to e.g. promote this new row to primary.
    const existing = row.routes || row.routesList || [];
    const scope = lockedType
      ? existing.filter(r => (r.messageType || r.message_type) === lockedType)
      : existing;
    const defaultPriority = scope.length > 0
      ? Math.max(...scope.map(r => r.priority || 0)) + 1
      : 1;
    this.setState({
      showAssignDialog: true,
      editMode: false,
      editRouteId: null,
      editRouteSmscId: '',
      assignTenantId: row.tenant_id || row.tenantId,
      assignTenantName: row.tenant_name || row.tenantName,
      assignOperatorId: '',
      assignMessageType: lockedType || 'transactional',
      assignLockMessageType: !!lockedType,
      assignPriority: defaultPriority,
      assignExistingRoutes: existing
    });
  }

  // Reassign the operator on a specific route line. Keeps the same message
  // type + priority slot; the admin just picks a different SMSC. Because the
  // route key is (tenant, type, operator), we set the new operator first then
  // delete the old route (chained in componentWillReceiveProps).
  openEditDialog = (row, route) => {
    const mt = route.messageType || route.message_type || 'transactional';
    this.setState({
      showAssignDialog: true,
      editMode: true,
      editRouteId: route.routeId || route.route_id,
      editRouteSmscId: route.smscId || route.smsc_id || '',
      assignTenantId: row.tenant_id || row.tenantId,
      assignTenantName: row.tenant_name || row.tenantName,
      assignOperatorId: '',
      assignMessageType: mt,
      assignLockMessageType: true,
      assignPriority: route.priority || 1,
      assignExistingRoutes: row.routes || row.routesList || []
    });
  }

  handleAssign = () => {
    if (!this.state.assignOperatorId) return;
    // Reassign: remember to delete the old route once the new operator is set.
    this.pendingRemoveRouteId = (this.state.editMode && this.state.editRouteId)
      ? this.state.editRouteId
      : null;
    this.setRouteReq = true;
    this.setState({showWait: true});
    this.props.setOperatorRoute({
      tenantId: this.state.assignTenantId,
      operatorId: this.state.assignOperatorId,
      productCode: 'sms',
      messageType: this.state.assignMessageType || 'transactional',
      priority: parseInt(this.state.assignPriority, 10) || 1,
      weight: 100
    });
  }

  openBulkDialog = () => {
    this.setState({showBulkDialog: true, bulkOperatorId: '', bulkExcludeRouted: true});
  }

  handleBulkAssign = () => {
    if (!this.state.bulkOperatorId) return;
    const excludeIds = this.state.bulkExcludeRouted
      ? this.state.tenants.filter(t => t.smsc_id || t.smscId).map(t => t.tenant_id || t.tenantId)
      : [];
    this.bulkRouteReq = true;
    this.setState({showWait: true});
    this.props.bulkSetOperatorRoute({
      operatorId: this.state.bulkOperatorId,
      productCode: 'sms',
      excludeTenantIds: excludeIds,
      priority: 1,
      weight: 100
    });
  }

  handleRemoveRoute = (routeId) => {
    if (!window.confirm('Remove this routing assignment?')) return;
    this.removeRouteReq = true;
    this.setState({showWait: true});
    this.props.removeOperatorRoute(routeId);
  }

  render() {
    const {loading, tenants, total, operators, searchText, showAssignDialog, showWait} = this.state;
    const activeOperators = operators.filter(op => op.is_active || op.isActive);
    // Count tenants that have at least one route. Prefer the new nested
    // `routes` array; fall back to the flat smsc_id/smscId for old API responses.
    const assigned = tenants.filter(r =>
      (Array.isArray(r.routes) && r.routes.length > 0) || r.smsc_id || r.smscId
    ).length;

    return (
      <div>
        <div style={{display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16}}>
          <div>
            <p style={{fontSize: 16, fontWeight: 500, margin: 0}}>Tenant Routing ({total})</p>
            <p style={{fontSize: 12, color: 'rgba(0,0,0,0.5)', margin: 0}}>
              Showing {tenants.length} of {total} tenants | Assigned: {assigned}
            </p>
          </div>
          <div style={{marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center'}}>
            <TextField
              size="small"
              placeholder="Search by name or org ID..."
              value={searchText}
              onChange={this.handleSearch}
              sx={{width: 280}}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment>
              }}
            />
            <Button variant="outlined" size="small" onClick={this.openBulkDialog}
              sx={{textTransform: 'none', fontWeight: 500}}>
              Bulk Assign
            </Button>
            <Tooltip title="Refresh">
              <IconButton onClick={this.handleRefresh}><Refresh /></IconButton>
            </Tooltip>
          </div>
        </div>

        {loading ? (
          <div style={{display: 'flex', justifyContent: 'center', padding: 40}}>
            <CircularProgress />
          </div>
        ) : (
          <Table
            column={[
              {name: 'Tenant', value: 'tenant_name', cell: (val, row) => val || row.tenantName},
              {name: 'Org ID', value: 'tenant_id', cell: (val, row) =>
                <Typography variant="caption" sx={{fontFamily: 'monospace', color: 'text.secondary'}}>{val || row.tenantId}</Typography>
              },
              {name: 'Routes', value: 'routes', cell: (val, row) => {
                // Prefer the new nested array. Fall back to the flat single-route
                // shape so the page doesn't go blank if a backend pinned to the
                // pre-multi-route API is ever served.
                let routes = val || row.routes || [];
                if ((!routes || routes.length === 0) && (row.smscId || row.smsc_id)) {
                  routes = [{
                    routeId: row.routeId || row.route_id,
                    smscId: row.smscId || row.smsc_id,
                    operatorName: row.operatorName || row.operator_name,
                    priority: 1,
                    weight: 100,
                    isPrimary: true
                  }];
                }
                if (!routes || routes.length === 0) {
                  return (
                    <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                      <Chip label="No tenant route (uses global)" size="small" variant="outlined" />
                      <Tooltip title="Add a route for this tenant">
                        <IconButton size="small" color="primary" onClick={() => this.openAssignDialog(row)}>
                          <Add fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  );
                }
                // Group by message_type — each type has its own primary (+ standbys).
                const TYPES = ['otp', 'transactional', 'promotional'];
                const byType = {};
                routes.forEach(r => {
                  const mt = r.messageType || r.message_type || '(any)';
                  (byType[mt] = byType[mt] || []).push(r);
                });
                const order = [...TYPES.filter(t => byType[t]), ...Object.keys(byType).filter(t => !TYPES.includes(t))];
                return (
                  <div style={{display: 'flex', flexDirection: 'column', gap: 6}}>
                    {order.map(mt => (
                      <div key={mt} style={{display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap'}}>
                        <Chip label={mt.toUpperCase()} size="small" variant="outlined" sx={{minWidth: 92, fontWeight: 600}} />
                        {byType[mt].slice().sort((a, b) => (a.priority || 0) - (b.priority || 0)).map(r => {
                          const rid = r.routeId || r.route_id;
                          const smsc = r.smscId || r.smsc_id;
                          return (
                            <span key={rid} style={{display: 'inline-flex', alignItems: 'center', gap: 2}}>
                              {/* onDelete renders the ✕ ON the chip → it is unambiguous
                                  which operator gets removed. */}
                              <Tooltip title={`Remove ${smsc} from ${mt.toUpperCase()} (priority ${r.priority ?? '?'})`}>
                                <Chip
                                  label={smsc}
                                  size="small"
                                  color={r.isPrimary ? 'primary' : 'default'}
                                  variant={r.isPrimary ? 'filled' : 'outlined'}
                                  onDelete={() => this.handleRemoveRoute(rid)}
                                />
                              </Tooltip>
                              <Typography variant="caption" color="text.secondary">
                                p={r.priority ?? '?'}{r.isPrimary ? ' • primary' : ' • standby'}
                              </Typography>
                              <Tooltip title={`Reassign ${mt.toUpperCase()} to a different operator (keeps priority ${r.priority ?? '?'})`}>
                                <IconButton size="small" onClick={() => this.openEditDialog(row, r)} sx={{p: 0.25}}>
                                  <Edit sx={{fontSize: 15}} />
                                </IconButton>
                              </Tooltip>
                            </span>
                          );
                        })}
                        <Tooltip title={`Add another operator for ${mt.toUpperCase()} (failover/standby)`}>
                          <IconButton size="small" color="primary" onClick={() => this.openAssignDialog(row, mt)} sx={{p: 0.25}}>
                            <Add sx={{fontSize: 17}} />
                          </IconButton>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                );
              }},
              {name: 'Status', value: 'status', cell: (val) =>
                <Chip label={val || 'unknown'} size="small" color={val === 'live' ? 'success' : 'default'} variant="outlined" />
              },
              {name: 'Actions', value: 'tenant_id', style: {width: 90}, cell: (val, row) => {
                // Per-route delete / reassign / per-type add-failover now live
                // INLINE on each route line in the Routes column (clearer which
                // operator each control affects). The only thing left here is
                // adding a route for a NEW message type (type-selectable dialog).
                return (
                  <Tooltip title="Add a route for a new message type (OTP / Transactional / Promotional)">
                    <IconButton size="small" color="primary" onClick={() => this.openAssignDialog(row)}>
                      <Add fontSize="small" />
                    </IconButton>
                  </Tooltip>
                );
              }}
            ]}
            serialNumber
            data={tenants}
            showPagination
            pageOptions={[25, 50, 100]}
            defaultPageSize={50}
          />
        )}

        {showAssignDialog &&
          <Dialog open={true} onClose={() => this.setState({showAssignDialog: false})} maxWidth="sm" fullWidth>
            <DialogTitle>
              {this.state.editMode
                ? `Reassign ${(this.state.assignMessageType || '').toUpperCase()} for ${this.state.assignTenantName}`
                : `Assign SMSC to ${this.state.assignTenantName}`}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {this.state.editMode
                  ? `Pick a different operator for ${(this.state.assignMessageType || '').toUpperCase()} `
                    + `at priority ${this.state.assignPriority}. The new operator is set first, then the `
                    + `previous one (${this.state.editRouteSmscId}) is removed from this slot.`
                  : 'Select which SMSC operator should handle SMS traffic for this tenant.'}
              </Typography>

              {!this.state.editMode && this.state.assignExistingRoutes && this.state.assignExistingRoutes.length > 0 && (
                <Alert severity="info" sx={{mb: 2}}>
                  This tenant already has <strong>{this.state.assignExistingRoutes.length}</strong> route(s):
                  <ul style={{margin: '6px 0 0', paddingLeft: 20, fontSize: 13}}>
                    {this.state.assignExistingRoutes.map(r => (
                      <li key={r.routeId || r.route_id}>
                        <code>{r.smscId || r.smsc_id}</code> — priority {r.priority ?? '?'}
                        {r.isPrimary ? ' (primary)' : ' (standby)'}
                      </li>
                    ))}
                  </ul>
                  Defaulting new route to priority <strong>{this.state.assignPriority}</strong> (standby).
                  Set priority to <strong>1</strong> below to promote this route to primary —
                  the SMS pipeline will switch to it once the resolver cache refreshes.
                </Alert>
              )}

              <Typography variant="caption" color="text.secondary">Message type</Typography>
              <Select
                fullWidth
                value={this.state.assignMessageType || 'transactional'}
                onChange={(e) => this.setState({assignMessageType: e.target.value})}
                size="small"
                sx={{mb: 2}}
                // Locked when adding a failover for a specific type (per-line +)
                // or reassigning — both operate on one known message type.
                disabled={this.state.assignLockMessageType}
              >
                <MenuItem value="otp">OTP</MenuItem>
                <MenuItem value="transactional">Transactional</MenuItem>
                <MenuItem value="promotional">Promotional</MenuItem>
              </Select>

              <Select
                fullWidth
                value={this.state.assignOperatorId}
                onChange={(e) => this.setState({assignOperatorId: e.target.value})}
                displayEmpty
                size="small"
                sx={{mb: 2}}
              >
                <MenuItem value="" disabled>Select an operator...</MenuItem>
                {activeOperators
                  // On reassign, exclude the operator already on this line so the
                  // pick is always a real change (avoids a no-op set+delete).
                  .filter(op => !(this.state.editMode && (op.smsc_id || op.smscId) === this.state.editRouteSmscId))
                  .map(op => (
                    <MenuItem key={op.id} value={op.id}>
                      {op.name} ({op.smsc_id || op.smscId})
                    </MenuItem>
                  ))}
              </Select>

              <TextField
                fullWidth
                size="small"
                label="Priority"
                type="number"
                inputProps={{min: 1, max: 99, step: 1}}
                value={this.state.assignPriority}
                onChange={(e) => this.setState({assignPriority: e.target.value})}
                helperText="Lower wins. 1 = primary (resolver picks this for outgoing SMS). 2+ = standby/failover."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({showAssignDialog: false})}>Cancel</Button>
              <Button variant="contained" disabled={!this.state.assignOperatorId} onClick={this.handleAssign}>
                {this.state.editMode ? 'Reassign' : 'Assign'}
              </Button>
            </DialogActions>
          </Dialog>
        }

        {this.state.showBulkDialog &&
          <Dialog open={true} onClose={() => this.setState({showBulkDialog: false})} maxWidth="sm" fullWidth>
            <DialogTitle>Bulk Assign SMSC to All Tenants</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                Assign a single SMSC operator to all tenants at once. Tenants with existing routes can be excluded.
              </Typography>
              <Select
                fullWidth
                value={this.state.bulkOperatorId}
                onChange={(e) => this.setState({bulkOperatorId: e.target.value})}
                displayEmpty
                sx={{mb: 2}}
              >
                <MenuItem value="" disabled>Select an operator...</MenuItem>
                {activeOperators.map(op => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.name} ({op.smsc_id || op.smscId})
                  </MenuItem>
                ))}
              </Select>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={this.state.bulkExcludeRouted}
                    onChange={(e) => this.setState({bulkExcludeRouted: e.target.checked})}
                  />
                }
                label="Exclude tenants that already have a route assigned"
              />
              {this.state.bulkOperatorId && (
                <Alert severity="info" sx={{mt: 2}}>
                  Will assign: <strong>{total - (this.state.bulkExcludeRouted ? assigned : 0)}</strong> tenants<br />
                  Will skip: <strong>{this.state.bulkExcludeRouted ? assigned : 0}</strong> tenants (already routed)
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.setState({showBulkDialog: false})}>Cancel</Button>
              <Button variant="contained" onClick={this.handleBulkAssign}
                disabled={!this.state.bulkOperatorId}>
                Assign All
              </Button>
            </DialogActions>
          </Dialog>
        }

        {showWait &&
          <Dialog open={true}><DialogContent><CircularProgress /></DialogContent></Dialog>
        }
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    operatorsList: state.manageOperators.listOperatorsResponse,
    tenantRoutesRes: state.manageOperators.listTenantRoutesResponse,
    setRouteRes: state.manageOperators.setRouteResponse,
    removeRouteRes: state.manageOperators.removeRouteResponse,
    bulkSetRouteRes: state.manageOperators.bulkSetRouteResponse
  };
}

export default connect(mapStateToProps, {
  listOperators, listTenantRoutes, setOperatorRoute, removeOperatorRoute, bulkSetOperatorRoute
})(TenantRouting);
